import { useJsApiLoader } from "@react-google-maps/api";
import { DeliveryAddressService } from "../services/DeliveryAddressService";

const libraries: ("places")[] = ["places"];

export interface IShippingConfig {
  minDistance: number;
  minTotal: number;
  basePrice: number;
  priceByMinute: number;
  priceByKm: number;
}

export interface ILatLng {
  lat: number;
  lng: number;
}

export interface IShippingResult {
  /** Precio final para esta orden (con el descuento de envío gratis ya aplicado, si corresponde). */
  price: number;
  /** Precio base por distancia, SIN el descuento de envío gratis. */
  rawPrice: number;
  distanceMeters: number;
  durationSeconds: number;
  durationText: string;
}

export interface IShippingQuoteParams {
  /** Sucursal desde la que sale el envío. */
  warehouseId: number;
  /** Punto de entrega; opcional si se manda addressId (dirección guardada). */
  destination?: ILatLng;
  /** Dirección guardada del cliente: el backend reusa/actualiza su cache por sucursal. */
  addressId?: number;
  /** Total del pedido sin envío; si se omite es solo cotización (sin envío gratis). */
  orderTotal?: number;
}

/**
 * config.minDistance ES el radio real (metros) desde la sucursal para el
 * envío gratis. Solo para dibujarlo en el mapa: el precio lo decide el backend.
 */
export const getFreeShippingRadius = (config: IShippingConfig): number => config.minDistance;

const formatDuration = (seconds: number): string => `${Math.round(seconds / 60)} min`;

/**
 * Cotiza el envío con el backend (/shipping/quote). La fórmula vive solo ahí
 * (WarehouseService) y es la misma que usan el chatbot y la app, para que un
 * pedido cueste lo mismo sin importar por dónde se capture.
 */
export const useShippingCalculator = () => {
  // Mismo id que Map.tsx/shippingQuote.tsx: @react-google-maps/api comparte
  // la carga del script entre todos los usos de este mismo id. Se sigue
  // necesitando para pintar el mapa y el autocompletado de direcciones.
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY ?? "",
    libraries,
  });

  const calculateShipping = async ({
    warehouseId,
    destination,
    addressId,
    orderTotal,
  }: IShippingQuoteParams): Promise<IShippingResult> => {
    const res = await DeliveryAddressService.quoteShipping({
      warehouse_id: warehouseId,
      delivery_address_id: addressId,
      lat: destination?.lat,
      lng: destination?.lng,
      order_total: orderTotal,
    });
    if (!res?.status || !res.data) {
      throw new Error(res?.message || "No se pudo calcular el costo de envío");
    }
    return {
      price: Number(res.data.shipping_cost),
      rawPrice: Number(res.data.raw_price),
      distanceMeters: Number(res.data.distance_meters),
      durationSeconds: Number(res.data.duration_seconds),
      durationText: formatDuration(Number(res.data.duration_seconds)),
    };
  };

  return { calculateShipping, isLoaded };
};
